import React from 'react';
import { clsx } from 'clsx';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import {
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { Task, TaskStatus, TaskPriority, TaskType } from '../../../types/task.types';
import { Avatar } from '../../ui';

interface TaskCardMiniProps {
  task: Task;
  onClick?: () => void;
  onComplete?: () => void;
  isDragging?: boolean;
  isActive?: boolean;
  className?: string;
}

const typeIcons: Record<TaskType, React.ReactNode> = {
  CALL: <PhoneIcon className="h-3.5 w-3.5" />,
  EMAIL: <EnvelopeIcon className="h-3.5 w-3.5" />,
  MEETING: <UserGroupIcon className="h-3.5 w-3.5" />,
  TODO: <CheckCircleIcon className="h-3.5 w-3.5" />,
  FOLLOW_UP: <ClockIcon className="h-3.5 w-3.5" />,
  CUSTOM: <CheckCircleIcon className="h-3.5 w-3.5" />,
};

const priorityColors: Record<TaskPriority, string> = {
  LOW: 'bg-gray-400',
  NORMAL: 'bg-blue-500',
  MEDIUM: 'bg-yellow-500',
  HIGH: 'bg-orange-500',
  URGENT: 'bg-red-500',
};

const typeColors: Record<TaskType, string> = {
  CALL: 'text-blue-600 bg-blue-100',
  EMAIL: 'text-purple-600 bg-purple-100',
  MEETING: 'text-green-600 bg-green-100',
  TODO: 'text-gray-600 bg-gray-100',
  FOLLOW_UP: 'text-orange-600 bg-orange-100',
  CUSTOM: 'text-indigo-600 bg-indigo-100',
};

const TaskCardMini: React.FC<TaskCardMiniProps> = ({
  task,
  onClick,
  onComplete,
  isDragging = false,
  isActive = false,
  className,
}) => {
  const isOverdue = task.dueDateTime && isPast(new Date(task.dueDateTime)) && task.status !== TaskStatus.COMPLETED;
  const isCompleted = task.status === TaskStatus.COMPLETED;

  const formatDueTime = (date: string) => {
    const d = new Date(date);
    if (isToday(d)) return format(d, 'h:mm a');
    if (isTomorrow(d)) return 'Tomorrow';
    return format(d, 'MMM d');
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onComplete?.();
  };

  return (
    <div
      onClick={onClick}
      draggable
      className={clsx(
        'group relative bg-white rounded-lg border border-gray-200 p-3 transition-all duration-200',
        'hover:shadow-md hover:border-primary-300 cursor-pointer',
        isDragging && 'shadow-xl rotate-3 scale-105 border-primary-400 ring-2 ring-primary-200',
        isActive && 'ring-2 ring-primary-500 border-primary-500',
        isCompleted && 'opacity-50 bg-gray-50',
        className
      )}
    >
      {/* Priority dot */}
      <div className={clsx(
        'absolute top-2 right-2 w-2 h-2 rounded-full',
        priorityColors[task.priority]
      )} />

      {/* Type icon */}
      <div className={clsx(
        'inline-flex items-center justify-center p-1.5 rounded-md mb-2',
        typeColors[task.type]
      )}>
        {typeIcons[task.type]}
      </div>

      {/* Title */}
      <h4 className={clsx(
        'font-medium text-sm text-gray-900 line-clamp-2 mb-2',
        isCompleted && 'line-through text-gray-500'
      )}>
        {task.title}
      </h4>

      {/* Footer */}
      <div className="flex items-center justify-between">
        {/* Due time */}
        {task.dueDateTime && (
          <span className={clsx(
            'text-xs font-medium',
            isOverdue ? 'text-red-600' : 'text-gray-500'
          )}>
            {formatDueTime(task.dueDateTime)}
          </span>
        )}

        {/* Assignee */}
        {task.assignments?.[0] && (
          <Avatar
            size="xs"
            name={task.assignments[0].user.firstName || task.assignments[0].user.email}
          />
        )}

        {/* Quick complete */}
        {!isCompleted && (
          <button
            onClick={handleComplete}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-green-100 rounded-full"
          >
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          </button>
        )}
        {isCompleted && (
          <CheckCircleSolidIcon className="h-4 w-4 text-green-500" />
        )}
      </div>
    </div>
  );
};

export default TaskCardMini;

